import { cn } from "@/utils/cn";

export interface SegmentedControlOption<T extends string> {
  value: T;
  label: string;
  count?: number;
  /** `danger` draws the count in red (e.g. tickets still waiting for an answer). */
  countTone?: "danger";
}

export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  size = "sm",
  className,
}: {
  options: SegmentedControlOption<T>[];
  value: T;
  onChange: (value: T) => void;
  size?: "sm" | "md";
  className?: string;
}) {
  return (
    <div
      role="tablist"
      className={cn(
        "inline-flex items-center rounded-lg border border-border bg-cream-100 p-0.5",
        className,
      )}
    >
      {options.map((opt) => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(opt.value)}
            className={cn(
              "rounded-md font-medium transition-all duration-150 select-none",
              size === "sm" ? "px-2.5 py-1 text-xs" : "px-3 py-1.5 text-sm",
              active
                ? "bg-teal-500 text-cream-50 shadow-sm font-semibold"
                : "text-ink-muted hover:bg-cream-200/70 hover:text-ink",
            )}
          >
            {opt.label}
            {opt.count !== undefined && (
              <span
                className={cn(
                  "ml-1.5 rounded-full px-1.5 py-0.2 text-[10px]",
                  opt.countTone === "danger"
                    ? "bg-danger-500 font-semibold text-cream-50"
                    : active
                      ? "bg-teal-700/40 text-cream-50"
                      : "bg-cream-200 text-ink-muted",
                )}
              >
                {opt.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
